import {
    AppstoreOutlined,
    BgColorsOutlined,
    CheckCircleOutlined,
    DeleteOutlined,
    DollarOutlined,
    EditOutlined,
    InfoCircleOutlined,
    MessageOutlined,
    PhoneOutlined,
    PictureOutlined,
    PlusOutlined,
    ReloadOutlined,
    SettingOutlined,
    StarOutlined,
} from '@ant-design/icons'
import {
    Button,
    Col,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Row,
    Select,
    Table,
    Tabs,
    Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React from 'react'
import { useTranslation } from 'react-i18next'
import $api from '../../../../shared/api/api'
import { applySiteTheme, SiteTheme } from '../../../../shared/lib/hooks/useSiteTheme/useSiteTheme'
import { StickyHeader } from '../../../../shared/ui/StickyHeader'
import cls from './AdminPage.module.scss'

type Contact = {
    id: string
    titleRu: string
    titleEn: string
    phone: string
    whatsappUrl: string
    whatsappLabel: string
    telegramUrl: string
    telegramLabel: string
}

type About = {
    id: string
    titleRu: string
    titleEn: string
    textRu: string
    textEn: string
    priceAmount: string
    minDaysValue: string
    mileageValue: string
    perDayLabelRu: string
    perDayLabelEn: string
    minDaysLabelRu: string
    minDaysLabelEn: string
    mileageLabelRu: string
    mileageLabelEn: string
}

type FeatureSection = { id: string; titleRu: string; titleEn: string }
type FeatureItem = {
    id: string
    sectionId: string
    textRu: string
    textEn: string
    sortOrder: number
}

type DetailsSection = { id: string; titleRu: string; titleEn: string }
type DetailItem = {
    id: string
    sectionId: string
    key: string
    labelRu: string
    labelEn: string
    contentRu: string
    contentEn: string
    sortOrder: number
}

type PriceSection = {
    id: string
    titleRu: string
    titleEn: string
    amount: string | number
    currency: string
    depositTextRu: string
    depositTextEn: string
    perDayTextRu: string
    perDayTextEn: string
}
type PriceIncludedItem = {
    id: string
    sectionId: string
    textRu: string
    textEn: string
    sortOrder: number
}

type GallerySection = {
    id: string
    titleRu: string
    titleEn: string
    backgroundColor?: string | null
}
type GalleryImage = {
    id: string
    sectionId: string
    imageUrl: string
    alt?: string | null
    sortOrder: number
}

type BenefitsSection = { id: string; titleRu: string; titleEn: string }
type BenefitItem = {
    id: string
    sectionId: string
    titleRu: string
    titleEn: string
    descriptionRu: string
    descriptionEn: string
    icon?: string | null
    sortOrder: number
}

type Review = {
    id: string
    name: string
    text: string
    imageUrl?: string | null
    approved: boolean
    createdAt: string
}

async function safeGet<T>(url: string, params?: Record<string, any>): Promise<T> {
    const res = await $api.get<T>(url, { params })
    return res.data
}

async function safePost<T>(url: string, body: any): Promise<T> {
    const res = await $api.post<T>(url, body)
    return res.data
}

async function safePatch<T>(url: string, body: any): Promise<T> {
    const res = await $api.patch<T>(url, body)
    return res.data
}

async function safeDelete<T>(url: string): Promise<T> {
    const res = await $api.delete<T>(url)
    return res.data
}

function useCrudList<T extends { id: string }>(opts: { list: () => Promise<T[]> }) {
    const [data, setData] = React.useState<T[]>([])
    const [loading, setLoading] = React.useState(false)
    const listRef = React.useRef(opts.list)
    listRef.current = opts.list

    const reload = React.useCallback(async () => {
        setLoading(true)
        try {
            const items = await listRef.current()
            setData(items)
        } finally {
            setLoading(false)
        }
    }, [])

    React.useEffect(() => {
        reload()
    }, [reload])

    return { data, setData, loading, reload }
}

function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < breakpoint)
    React.useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < breakpoint)
        window.addEventListener('resize', handler)
        return () => window.removeEventListener('resize', handler)
    }, [breakpoint])
    return isMobile
}

const AdminPage: React.FC = () => {
    const { t } = useTranslation()
    const isMobile = useIsMobile()

    // CONTACT
    const contact = useCrudList<Contact>({ list: () => safeGet<Contact[]>('/contact') })
    const [contactModalOpen, setContactModalOpen] = React.useState(false)
    const [contactEditing, setContactEditing] = React.useState<Contact | null>(null)
    const [contactForm] = Form.useForm()

    // ABOUT
    const about = useCrudList<About>({ list: () => safeGet<About[]>('/about') })
    const [aboutModalOpen, setAboutModalOpen] = React.useState(false)
    const [aboutEditing, setAboutEditing] = React.useState<About | null>(null)
    const [aboutForm] = Form.useForm()

    // FEATURES
    const featuresSections = useCrudList<FeatureSection>({
        list: () =>
            safeGet<any[]>('/features').then((rows) =>
                rows.map((s) => ({ id: s.id, titleRu: s.titleRu, titleEn: s.titleEn }))
            ),
    })
    const [featuresSectionId, setFeaturesSectionId] = React.useState<string | null>(null)
    const [featureItems, setFeatureItems] = React.useState<FeatureItem[]>([])
    const [featureItemsLoading, setFeatureItemsLoading] = React.useState(false)
    const [featureSectionModalOpen, setFeatureSectionModalOpen] = React.useState(false)
    const [featureSectionEditing, setFeatureSectionEditing] = React.useState<FeatureSection | null>(null)
    const [featureSectionForm] = Form.useForm()
    const [featureItemModalOpen, setFeatureItemModalOpen] = React.useState(false)
    const [featureItemEditing, setFeatureItemEditing] = React.useState<FeatureItem | null>(null)
    const [featureItemForm] = Form.useForm()

    const reloadFeatureItems = React.useCallback(async () => {
        setFeatureItemsLoading(true)
        try {
            const items = await safeGet<FeatureItem[]>('/features/items', {
                sectionId: featuresSectionId ?? undefined,
            })
            setFeatureItems(items)
        } finally {
            setFeatureItemsLoading(false)
        }
    }, [featuresSectionId])

    React.useEffect(() => {
        if (featuresSectionId) reloadFeatureItems()
        else setFeatureItems([])
    }, [featuresSectionId, reloadFeatureItems])

    // DETAILS
    const detailsSections = useCrudList<DetailsSection>({
        list: () =>
            safeGet<any[]>('/details').then((rows) =>
                rows.map((s) => ({ id: s.id, titleRu: s.titleRu, titleEn: s.titleEn }))
            ),
    })
    const [detailsSectionId, setDetailsSectionId] = React.useState<string | null>(null)
    const [detailItems, setDetailItems] = React.useState<DetailItem[]>([])
    const [detailItemsLoading, setDetailItemsLoading] = React.useState(false)
    const [detailsSectionModalOpen, setDetailsSectionModalOpen] = React.useState(false)
    const [detailsSectionEditing, setDetailsSectionEditing] = React.useState<DetailsSection | null>(null)
    const [detailsSectionForm] = Form.useForm()
    const [detailItemModalOpen, setDetailItemModalOpen] = React.useState(false)
    const [detailItemEditing, setDetailItemEditing] = React.useState<DetailItem | null>(null)
    const [detailItemForm] = Form.useForm()

    const reloadDetailItems = React.useCallback(async () => {
        setDetailItemsLoading(true)
        try {
            const items = await safeGet<DetailItem[]>('/details/items', {
                sectionId: detailsSectionId ?? undefined,
            })
            setDetailItems(items)
        } finally {
            setDetailItemsLoading(false)
        }
    }, [detailsSectionId])

    React.useEffect(() => {
        if (detailsSectionId) reloadDetailItems()
        else setDetailItems([])
    }, [detailsSectionId, reloadDetailItems])

    // PRICE
    const priceSections = useCrudList<PriceSection>({
        list: () =>
            safeGet<any[]>('/price').then((rows) =>
                rows.map((s) => ({
                    id: s.id,
                    titleRu: s.titleRu,
                    titleEn: s.titleEn,
                    amount: s.amount,
                    currency: s.currency,
                    depositTextRu: s.depositTextRu,
                    depositTextEn: s.depositTextEn,
                    perDayTextRu: s.perDayTextRu,
                    perDayTextEn: s.perDayTextEn,
                }))
            ),
    })
    const [priceSectionId, setPriceSectionId] = React.useState<string | null>(null)
    const [priceItems, setPriceItems] = React.useState<PriceIncludedItem[]>([])
    const [priceItemsLoading, setPriceItemsLoading] = React.useState(false)
    const [priceSectionModalOpen, setPriceSectionModalOpen] = React.useState(false)
    const [priceSectionEditing, setPriceSectionEditing] = React.useState<PriceSection | null>(null)
    const [priceSectionForm] = Form.useForm()
    const [priceItemModalOpen, setPriceItemModalOpen] = React.useState(false)
    const [priceItemEditing, setPriceItemEditing] = React.useState<PriceIncludedItem | null>(null)
    const [priceItemForm] = Form.useForm()

    const reloadPriceItems = React.useCallback(async () => {
        setPriceItemsLoading(true)
        try {
            const items = await safeGet<PriceIncludedItem[]>('/price/items', {
                sectionId: priceSectionId ?? undefined,
            })
            setPriceItems(items)
        } finally {
            setPriceItemsLoading(false)
        }
    }, [priceSectionId])

    React.useEffect(() => {
        if (priceSectionId) reloadPriceItems()
        else setPriceItems([])
    }, [priceSectionId, reloadPriceItems])

    // GALLERY
    const gallerySections = useCrudList<GallerySection>({
        list: () =>
            safeGet<any[]>('/gallery').then((rows) =>
                rows.map((s) => ({
                    id: s.id,
                    titleRu: s.titleRu,
                    titleEn: s.titleEn,
                    backgroundColor: s.backgroundColor,
                }))
            ),
    })
    const [gallerySectionId, setGallerySectionId] = React.useState<string | null>(null)
    const [galleryImages, setGalleryImages] = React.useState<GalleryImage[]>([])
    const [galleryImagesLoading, setGalleryImagesLoading] = React.useState(false)
    const [gallerySectionModalOpen, setGallerySectionModalOpen] = React.useState(false)
    const [gallerySectionEditing, setGallerySectionEditing] = React.useState<GallerySection | null>(null)
    const [gallerySectionForm] = Form.useForm()
    const [galleryImageModalOpen, setGalleryImageModalOpen] = React.useState(false)
    const [galleryImageEditing, setGalleryImageEditing] = React.useState<GalleryImage | null>(null)
    const [galleryImageForm] = Form.useForm()
    const [galleryImageFile, setGalleryImageFile] = React.useState<File | null>(null)
    const galleryFileInputRef = React.useRef<HTMLInputElement>(null)

    React.useEffect(() => {
        if (!galleryImageModalOpen) {
            setGalleryImageFile(null)
            if (galleryFileInputRef.current) galleryFileInputRef.current.value = ''
        }
    }, [galleryImageModalOpen])

    const reloadGalleryImages = React.useCallback(async () => {
        setGalleryImagesLoading(true)
        try {
            const items = await safeGet<GalleryImage[]>('/gallery/images', {
                sectionId: gallerySectionId ?? undefined,
            })
            setGalleryImages(items)
        } finally {
            setGalleryImagesLoading(false)
        }
    }, [gallerySectionId])

    React.useEffect(() => {
        if (gallerySectionId) reloadGalleryImages()
        else setGalleryImages([])
    }, [gallerySectionId, reloadGalleryImages])

    // BENEFITS
    const benefitsSections = useCrudList<BenefitsSection>({
        list: () =>
            safeGet<any[]>('/benefits').then((rows) =>
                rows.map((s) => ({ id: s.id, titleRu: s.titleRu, titleEn: s.titleEn }))
            ),
    })
    const [benefitsSectionId, setBenefitsSectionId] = React.useState<string | null>(null)
    const [benefitItems, setBenefitItems] = React.useState<BenefitItem[]>([])
    const [benefitItemsLoading, setBenefitItemsLoading] = React.useState(false)
    const [benefitsSectionModalOpen, setBenefitsSectionModalOpen] = React.useState(false)
    const [benefitsSectionEditing, setBenefitsSectionEditing] = React.useState<BenefitsSection | null>(null)
    const [benefitsSectionForm] = Form.useForm()
    const [benefitItemModalOpen, setBenefitItemModalOpen] = React.useState(false)
    const [benefitItemEditing, setBenefitItemEditing] = React.useState<BenefitItem | null>(null)
    const [benefitItemForm] = Form.useForm()

    const reloadBenefitItems = React.useCallback(async () => {
        setBenefitItemsLoading(true)
        try {
            const items = await safeGet<BenefitItem[]>('/benefits/items', {
                sectionId: benefitsSectionId ?? undefined,
            })
            setBenefitItems(items)
        } finally {
            setBenefitItemsLoading(false)
        }
    }, [benefitsSectionId])

    React.useEffect(() => {
        if (benefitsSectionId) reloadBenefitItems()
        else setBenefitItems([])
    }, [benefitsSectionId, reloadBenefitItems])

    // REVIEWS
    const pendingReviews = useCrudList<Review>({
        list: () => safeGet<Review[]>('/reviews/pending'),
    })
    const [approvingId, setApprovingId] = React.useState<string | null>(null)

    // ── Contact Tab ──────────────────────────────────────────────
    const ContactTab = (
        <div className={cls.tabContent}>
            <div className={cls.sectionCard}>
                <div className={cls.cardHeader}>
                    <span className={cls.cardHeaderTitle}>{t('admin.tabs.contact')}</span>
                    <div className={cls.cardHeaderActions}>
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setContactEditing(null)
                                contactForm.resetFields()
                                setContactModalOpen(true)
                            }}
                        >
                            {t('admin.actions.create')}
                        </Button>
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={contact.reload}
                            loading={contact.loading}
                        >
                            {t('admin.actions.refresh')}
                        </Button>
                    </div>
                </div>
                <div className={cls.tableWrapper}>
                    <Table<Contact>
                        rowKey="id"
                        loading={contact.loading}
                        dataSource={contact.data}
                        pagination={false}
                        size="middle"
                        scroll={{ x: 520 }}
                        columns={[
                            { title: t('admin.fields.titleRu'), dataIndex: 'titleRu', ellipsis: true },
                            { title: t('admin.fields.titleEn'), dataIndex: 'titleEn', ellipsis: true },
                            { title: t('admin.fields.phone'), dataIndex: 'phone', width: 150 },
                            {
                                title: '',
                                width: 90,
                                fixed: 'right' as const,
                                render: (_, record) => (
                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => {
                                                setContactEditing(record)
                                                contactForm.setFieldsValue(record)
                                                setContactModalOpen(true)
                                            }}
                                        />
                                        <Popconfirm
                                            title={t('admin.confirm.deleteRecord')}
                                            okText={t('admin.confirm.yes')}
                                            cancelText={t('admin.confirm.no')}
                                            onConfirm={async () => {
                                                await safeDelete(`/contact/${record.id}`)
                                                await contact.reload()
                                            }}
                                        >
                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            </div>

            <Modal
                open={contactModalOpen}
                title={
                    contactEditing
                        ? t('admin.modal.editTitle', { entity: t('admin.tabs.contact') })
                        : t('admin.modal.createTitle', { entity: t('admin.tabs.contact') })
                }
                onCancel={() => setContactModalOpen(false)}
                onOk={async () => {
                    const values = await contactForm.validateFields()
                    if (contactEditing) {
                        await safePatch(`/contact/${contactEditing.id}`, values)
                    } else {
                        await safePost('/contact', values)
                    }
                    setContactModalOpen(false)
                    await contact.reload()
                }}
                okText={t('admin.actions.save')}
                width={isMobile ? '95vw' : 560}
            >
                <Form form={contactForm} layout="vertical">
                    <Row gutter={12}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="titleRu" label={t('admin.fields.titleRu')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="titleEn" label={t('admin.fields.titleEn')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="phone" label={t('admin.fields.phone')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col xs={24} sm={16}>
                            <Form.Item name="whatsappUrl" label={t('admin.fields.whatsappUrl')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="whatsappLabel" label={t('admin.fields.whatsappLabel')}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col xs={24} sm={16}>
                            <Form.Item name="telegramUrl" label={t('admin.fields.telegramUrl')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="telegramLabel" label={t('admin.fields.telegramLabel')}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    )

    // ── About Tab ────────────────────────────────────────────────
    const AboutTab = (
        <div className={cls.tabContent}>
            <div className={cls.sectionCard}>
                <div className={cls.cardHeader}>
                    <span className={cls.cardHeaderTitle}>{t('admin.tabs.about')}</span>
                    <div className={cls.cardHeaderActions}>
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setAboutEditing(null)
                                aboutForm.resetFields()
                                setAboutModalOpen(true)
                            }}
                        >
                            {t('admin.actions.create')}
                        </Button>
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={about.reload}
                            loading={about.loading}
                        >
                            {t('admin.actions.refresh')}
                        </Button>
                    </div>
                </div>
                <div className={cls.tableWrapper}>
                    <Table<About>
                        rowKey="id"
                        loading={about.loading}
                        dataSource={about.data}
                        pagination={false}
                        size="middle"
                        scroll={{ x: 400 }}
                        columns={[
                            { title: t('admin.fields.titleRu'), dataIndex: 'titleRu', ellipsis: true },
                            { title: t('admin.fields.titleEn'), dataIndex: 'titleEn', ellipsis: true },
                            {
                                title: '',
                                width: 90,
                                fixed: 'right' as const,
                                render: (_, record) => (
                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => {
                                                setAboutEditing(record)
                                                aboutForm.setFieldsValue(record)
                                                setAboutModalOpen(true)
                                            }}
                                        />
                                        <Popconfirm
                                            title={t('admin.confirm.deleteRecord')}
                                            okText={t('admin.confirm.yes')}
                                            cancelText={t('admin.confirm.no')}
                                            onConfirm={async () => {
                                                await safeDelete(`/about/${record.id}`)
                                                await about.reload()
                                            }}
                                        >
                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            </div>

            <Modal
                open={aboutModalOpen}
                title={
                    aboutEditing
                        ? t('admin.modal.editTitle', { entity: t('admin.tabs.about') })
                        : t('admin.modal.createTitle', { entity: t('admin.tabs.about') })
                }
                onCancel={() => setAboutModalOpen(false)}
                onOk={async () => {
                    const values = await aboutForm.validateFields()
                    if (aboutEditing) {
                        await safePatch(`/about/${aboutEditing.id}`, values)
                    } else {
                        await safePost('/about', values)
                    }
                    setAboutModalOpen(false)
                    await about.reload()
                }}
                okText={t('admin.actions.save')}
                width={isMobile ? '95vw' : 800}
            >
                <Form form={aboutForm} layout="vertical">
                    <Row gutter={12}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="titleRu" label={t('admin.fields.titleRu')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="titleEn" label={t('admin.fields.titleEn')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="textRu" label={t('admin.fields.textRu')} rules={[{ required: true }]}>
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="textEn" label={t('admin.fields.textEn')} rules={[{ required: true }]}>
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col xs={24} sm={8}>
                            <Form.Item name="priceAmount" label={t('admin.fields.priceAmount')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="minDaysValue" label={t('admin.fields.minDaysValue')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="mileageValue" label={t('admin.fields.mileageValue')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="perDayLabelRu" label={t('admin.fields.perDayLabelRu')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="perDayLabelEn" label={t('admin.fields.perDayLabelEn')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="minDaysLabelRu" label={t('admin.fields.minDaysLabelRu')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="minDaysLabelEn" label={t('admin.fields.minDaysLabelEn')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="mileageLabelRu" label={t('admin.fields.mileageLabelRu')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="mileageLabelEn" label={t('admin.fields.mileageLabelEn')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    )

    // ── Generic section + items CRUD tab ─────────────────────────
    const makeSectionCrudTab = <S extends { id: string; titleRu: string; titleEn: string }>(opts: {
        title: string
        sections: ReturnType<typeof useCrudList<S>>
        selectedId: string | null
        setSelectedId: (id: string | null) => void
        sectionModalOpen: boolean
        setSectionModalOpen: (v: boolean) => void
        sectionEditing: S | null
        setSectionEditing: (v: S | null) => void
        sectionForm: any
        createSection: (values: any) => Promise<any>
        updateSection: (id: string, values: any) => Promise<any>
        removeSection: (id: string) => Promise<any>
        itemsTitle: string
        items: any[]
        itemsLoading: boolean
        reloadItems: () => Promise<void>
        itemModalOpen: boolean
        setItemModalOpen: (v: boolean) => void
        itemEditing: any | null
        setItemEditing: (v: any | null) => void
        itemForm: any
        createItem: (values: any) => Promise<any>
        updateItem: (id: string, values: any) => Promise<any>
        removeItem: (id: string) => Promise<any>
        sectionIdField: string
        itemColumns: ColumnsType<any>
        sectionExtraFields?: React.ReactNode
        itemExtraFields?: React.ReactNode
    }) => {
        const sectionColumns: ColumnsType<S> = [
            { title: t('admin.fields.titleRu'), dataIndex: 'titleRu', ellipsis: true },
            { title: t('admin.fields.titleEn'), dataIndex: 'titleEn', ellipsis: true },
            {
                title: '',
                width: 160,
                fixed: 'right' as const,
                render: (_, record) => (
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <Button
                            size="small"
                            onClick={() => opts.setSelectedId(record.id)}
                        >
                            {t('admin.actions.select')}
                        </Button>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                                opts.setSectionEditing(record)
                                opts.sectionForm.setFieldsValue(record)
                                opts.setSectionModalOpen(true)
                            }}
                        />
                        <Popconfirm
                            title={t('admin.confirm.deleteSection')}
                            okText={t('admin.confirm.yes')}
                            cancelText={t('admin.confirm.no')}
                            onConfirm={async () => {
                                await opts.removeSection(record.id)
                                opts.setSelectedId(null)
                                await opts.sections.reload()
                            }}
                        >
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </div>
                ),
            },
        ]

        return (
            <div className={cls.tabContent}>
                {/* Sections card */}
                <div className={cls.sectionCard}>
                    <div className={cls.cardHeader}>
                        <span className={cls.cardHeaderTitle}>{opts.title}</span>
                        <div className={cls.cardHeaderActions}>
                            <Button
                                type="primary"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    opts.setSectionEditing(null)
                                    opts.sectionForm.resetFields()
                                    opts.setSectionModalOpen(true)
                                }}
                            >
                                {t('admin.actions.createSection')}
                            </Button>
                            <Button
                                size="small"
                                icon={<ReloadOutlined />}
                                onClick={opts.sections.reload}
                                loading={opts.sections.loading}
                            >
                                {t('admin.actions.refresh')}
                            </Button>
                        </div>
                    </div>
                    <div className={cls.tableWrapper}>
                        <Table<S>
                            rowKey="id"
                            loading={opts.sections.loading}
                            dataSource={opts.sections.data}
                            pagination={false}
                            size="middle"
                            scroll={{ x: 480 }}
                            columns={sectionColumns}
                        />
                    </div>
                </div>

                {/* Section modal */}
                <Modal
                    open={opts.sectionModalOpen}
                    title={
                        opts.sectionEditing
                            ? t('admin.modal.editTitle', { entity: opts.title })
                            : t('admin.modal.createTitle', { entity: opts.title })
                    }
                    onCancel={() => opts.setSectionModalOpen(false)}
                    onOk={async () => {
                        const values = await opts.sectionForm.validateFields()
                        if (opts.sectionEditing) {
                            await opts.updateSection(opts.sectionEditing.id, values)
                        } else {
                            await opts.createSection(values)
                        }
                        opts.setSectionModalOpen(false)
                        await opts.sections.reload()
                    }}
                    okText={t('admin.actions.save')}
                    width={isMobile ? '95vw' : 520}
                >
                    <Form form={opts.sectionForm} layout="vertical">
                        <Row gutter={12}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="titleRu" label={t('admin.fields.titleRu')} rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item name="titleEn" label={t('admin.fields.titleEn')} rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        {opts.sectionExtraFields}
                    </Form>
                </Modal>

                {/* Items card */}
                <div className={cls.subsectionCard}>
                    <div className={cls.subsectionHeader}>
                        <span className={cls.subsectionLabel}>{opts.itemsTitle}</span>
                        {opts.selectedId && (
                            <span className={cls.subsectionIdBadge}>{opts.selectedId}</span>
                        )}
                        <div className={cls.subsectionActions}>
                            <Select
                                className={cls.sectionSelect}
                                placeholder={t('admin.items.selectSection')}
                                value={opts.selectedId ?? undefined}
                                onChange={(v) => opts.setSelectedId(v)}
                                size="small"
                                options={opts.sections.data.map((s) => ({
                                    value: s.id,
                                    label: `${s.titleRu} / ${s.titleEn}`,
                                }))}
                            />
                            <Button
                                type="primary"
                                size="small"
                                icon={<PlusOutlined />}
                                disabled={!opts.selectedId}
                                onClick={() => {
                                    if (!opts.selectedId) return
                                    opts.setItemEditing(null)
                                    opts.itemForm.resetFields()
                                    opts.itemForm.setFieldsValue({
                                        [opts.sectionIdField]: opts.selectedId,
                                        sortOrder: 0,
                                    })
                                    opts.setItemModalOpen(true)
                                }}
                            >
                                {t('admin.actions.add')}
                            </Button>
                            <Button
                                size="small"
                                icon={<ReloadOutlined />}
                                onClick={opts.reloadItems}
                                loading={opts.itemsLoading}
                            >
                                {t('admin.actions.refresh')}
                            </Button>
                        </div>
                    </div>
                    <div className={cls.tableWrapper}>
                        <Table
                            rowKey="id"
                            loading={opts.itemsLoading}
                            dataSource={opts.items}
                            pagination={false}
                            size="middle"
                            scroll={{ x: 500 }}
                            columns={[
                                ...opts.itemColumns,
                                {
                                    title: '',
                                    width: 80,
                                    fixed: 'right' as const,
                                    render: (_, record: any) => (
                                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                            <Button
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={() => {
                                                    opts.setItemEditing(record)
                                                    opts.itemForm.setFieldsValue(record)
                                                    opts.setItemModalOpen(true)
                                                }}
                                            />
                                            <Popconfirm
                                                title={t('admin.confirm.deleteItem')}
                                                okText={t('admin.confirm.yes')}
                                                cancelText={t('admin.confirm.no')}
                                                onConfirm={async () => {
                                                    await opts.removeItem(record.id)
                                                    await opts.reloadItems()
                                                }}
                                            >
                                                <Button size="small" danger icon={<DeleteOutlined />} />
                                            </Popconfirm>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    </div>
                </div>

                {/* Item modal */}
                <Modal
                    open={opts.itemModalOpen}
                    title={opts.itemEditing ? t('admin.modal.editItem') : t('admin.modal.createItem')}
                    onCancel={() => opts.setItemModalOpen(false)}
                    onOk={async () => {
                        const values = await opts.itemForm.validateFields()
                        if (opts.itemEditing) {
                            await opts.updateItem(opts.itemEditing.id, values)
                        } else {
                            await opts.createItem(values)
                        }
                        opts.setItemModalOpen(false)
                        await opts.reloadItems()
                    }}
                    okText={t('admin.actions.save')}
                    width={isMobile ? '95vw' : 680}
                >
                    <Form form={opts.itemForm} layout="vertical">
                        <Form.Item
                            name={opts.sectionIdField}
                            label={t('admin.fields.sectionId')}
                            rules={[{ required: true }]}
                        >
                            <Input disabled />
                        </Form.Item>
                        {opts.itemExtraFields}
                    </Form>
                </Modal>
            </div>
        )
    }

    // ── Section CRUD tabs ────────────────────────────────────────
    const FeaturesTab = makeSectionCrudTab<FeatureSection>({
        title: t('admin.tabs.features'),
        sections: featuresSections,
        selectedId: featuresSectionId,
        setSelectedId: setFeaturesSectionId,
        sectionModalOpen: featureSectionModalOpen,
        setSectionModalOpen: setFeatureSectionModalOpen,
        sectionEditing: featureSectionEditing,
        setSectionEditing: setFeatureSectionEditing,
        sectionForm: featureSectionForm,
        createSection: (values) => safePost('/features', values),
        updateSection: (id, values) => safePatch(`/features/${id}`, values),
        removeSection: (id) => safeDelete(`/features/${id}`),
        itemsTitle: t('admin.items.items'),
        items: featureItems,
        itemsLoading: featureItemsLoading,
        reloadItems: reloadFeatureItems,
        itemModalOpen: featureItemModalOpen,
        setItemModalOpen: setFeatureItemModalOpen,
        itemEditing: featureItemEditing,
        setItemEditing: setFeatureItemEditing,
        itemForm: featureItemForm,
        createItem: (values) => safePost('/features/items', values),
        updateItem: (id, values) => safePatch(`/features/items/${id}`, values),
        removeItem: (id) => safeDelete(`/features/items/${id}`),
        sectionIdField: 'sectionId',
        itemColumns: [
            { title: t('admin.fields.textRu'), dataIndex: 'textRu', ellipsis: true },
            { title: t('admin.fields.textEn'), dataIndex: 'textEn', ellipsis: true },
            { title: t('admin.fields.sortOrder'), dataIndex: 'sortOrder', width: 80 },
        ],
        itemExtraFields: (
            <>
                <Form.Item name="textRu" label={t('admin.fields.textRu')} rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="textEn" label={t('admin.fields.textEn')} rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="sortOrder" label={t('admin.fields.sortOrder')}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
            </>
        ),
    })

    const DetailsTab = makeSectionCrudTab<DetailsSection>({
        title: t('admin.tabs.details'),
        sections: detailsSections,
        selectedId: detailsSectionId,
        setSelectedId: setDetailsSectionId,
        sectionModalOpen: detailsSectionModalOpen,
        setSectionModalOpen: setDetailsSectionModalOpen,
        sectionEditing: detailsSectionEditing,
        setSectionEditing: setDetailsSectionEditing,
        sectionForm: detailsSectionForm,
        createSection: (values) => safePost('/details', values),
        updateSection: (id, values) => safePatch(`/details/${id}`, values),
        removeSection: (id) => safeDelete(`/details/${id}`),
        itemsTitle: t('admin.items.items'),
        items: detailItems,
        itemsLoading: detailItemsLoading,
        reloadItems: reloadDetailItems,
        itemModalOpen: detailItemModalOpen,
        setItemModalOpen: setDetailItemModalOpen,
        itemEditing: detailItemEditing,
        setItemEditing: setDetailItemEditing,
        itemForm: detailItemForm,
        createItem: (values) => safePost('/details/items', values),
        updateItem: (id, values) => safePatch(`/details/items/${id}`, values),
        removeItem: (id) => safeDelete(`/details/items/${id}`),
        sectionIdField: 'sectionId',
        itemColumns: [
            { title: t('admin.fields.key'), dataIndex: 'key', width: 110 },
            { title: t('admin.fields.labelRu'), dataIndex: 'labelRu', ellipsis: true },
            { title: t('admin.fields.labelEn'), dataIndex: 'labelEn', ellipsis: true },
            { title: t('admin.fields.sortOrder'), dataIndex: 'sortOrder', width: 80 },
        ],
        itemExtraFields: (
            <>
                <Form.Item name="key" label={t('admin.fields.key')} rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="labelRu" label={t('admin.fields.labelRu')} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="labelEn" label={t('admin.fields.labelEn')} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="contentRu" label={t('admin.fields.contentRu')} rules={[{ required: true }]}>
                            <Input.TextArea rows={3} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="contentEn" label={t('admin.fields.contentEn')} rules={[{ required: true }]}>
                            <Input.TextArea rows={3} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="sortOrder" label={t('admin.fields.sortOrder')}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
            </>
        ),
    })

    const PriceTab = makeSectionCrudTab<PriceSection>({
        title: t('admin.tabs.price'),
        sections: priceSections as any,
        selectedId: priceSectionId,
        setSelectedId: setPriceSectionId,
        sectionModalOpen: priceSectionModalOpen,
        setSectionModalOpen: setPriceSectionModalOpen,
        sectionEditing: priceSectionEditing as any,
        setSectionEditing: setPriceSectionEditing as any,
        sectionForm: priceSectionForm,
        createSection: (values) => safePost('/price', values),
        updateSection: (id, values) => safePatch(`/price/${id}`, values),
        removeSection: (id) => safeDelete(`/price/${id}`),
        itemsTitle: t('admin.items.includedItems'),
        items: priceItems,
        itemsLoading: priceItemsLoading,
        reloadItems: reloadPriceItems,
        itemModalOpen: priceItemModalOpen,
        setItemModalOpen: setPriceItemModalOpen,
        itemEditing: priceItemEditing,
        setItemEditing: setPriceItemEditing,
        itemForm: priceItemForm,
        createItem: (values) => safePost('/price/items', values),
        updateItem: (id, values) => safePatch(`/price/items/${id}`, values),
        removeItem: (id) => safeDelete(`/price/items/${id}`),
        sectionIdField: 'sectionId',
        itemColumns: [
            { title: t('admin.fields.textRu'), dataIndex: 'textRu', ellipsis: true },
            { title: t('admin.fields.textEn'), dataIndex: 'textEn', ellipsis: true },
            { title: t('admin.fields.sortOrder'), dataIndex: 'sortOrder', width: 80 },
        ],
        sectionExtraFields: (
            <>
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="amount" label={t('admin.fields.amount')} rules={[{ required: true }]}>
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="currency" label={t('admin.fields.currency')}>
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="depositTextRu" label={t('admin.fields.depositTextRu')} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="depositTextEn" label={t('admin.fields.depositTextEn')} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="perDayTextRu" label={t('admin.fields.perDayTextRu')} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="perDayTextEn" label={t('admin.fields.perDayTextEn')} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
            </>
        ),
        itemExtraFields: (
            <>
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="textRu" label={t('admin.fields.textRu')} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="textEn" label={t('admin.fields.textEn')} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="sortOrder" label={t('admin.fields.sortOrder')}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
            </>
        ),
    })

    const GalleryTab = makeSectionCrudTab<GallerySection>({
        title: t('admin.tabs.gallery'),
        sections: gallerySections as any,
        selectedId: gallerySectionId,
        setSelectedId: setGallerySectionId,
        sectionModalOpen: gallerySectionModalOpen,
        setSectionModalOpen: setGallerySectionModalOpen,
        sectionEditing: gallerySectionEditing as any,
        setSectionEditing: setGallerySectionEditing as any,
        sectionForm: gallerySectionForm,
        createSection: (values) => safePost('/gallery', values),
        updateSection: (id, values) => safePatch(`/gallery/${id}`, values),
        removeSection: (id) => safeDelete(`/gallery/${id}`),
        itemsTitle: t('admin.items.images'),
        items: galleryImages,
        itemsLoading: galleryImagesLoading,
        reloadItems: reloadGalleryImages,
        itemModalOpen: galleryImageModalOpen,
        setItemModalOpen: setGalleryImageModalOpen,
        itemEditing: galleryImageEditing,
        setItemEditing: setGalleryImageEditing,
        itemForm: galleryImageForm,
        createItem: async (values) => {
            if (!galleryImageFile) {
                message.error(t('admin.items.noImageError'))
                throw new Error('No file selected')
            }
            const fd = new FormData()
            fd.append('sectionId', values.sectionId)
            if (values.alt) fd.append('alt', values.alt)
            if (values.sortOrder != null) fd.append('sortOrder', String(values.sortOrder))
            fd.append('image', galleryImageFile)
            setGalleryImageFile(null)
            const res = await $api.post('/gallery/images', fd)
            return res.data
        },
        updateItem: async (id, values) => {
            const fd = new FormData()
            if (values.alt !== undefined) fd.append('alt', values.alt ?? '')
            if (values.sortOrder != null) fd.append('sortOrder', String(values.sortOrder))
            if (galleryImageFile) {
                fd.append('image', galleryImageFile)
                setGalleryImageFile(null)
            }
            const res = await $api.patch(`/gallery/images/${id}`, fd)
            return res.data
        },
        removeItem: (id) => safeDelete(`/gallery/images/${id}`),
        sectionIdField: 'sectionId',
        itemColumns: [
            {
                title: 'Image',
                dataIndex: 'imageUrl',
                width: 72,
                render: (url: string) => {
                    if (!url) return null
                    const serverUrl = (process.env.REACT_APP_API_URL ?? '').replace(/\/api\/?$/, '')
                    const fullUrl = url.startsWith('http') ? url : `${serverUrl}${url}`
                    return (
                        <img
                            src={fullUrl}
                            alt=""
                            style={{ width: 56, height: 38, objectFit: 'cover', borderRadius: 6 }}
                        />
                    )
                },
            },
            { title: t('admin.fields.alt'), dataIndex: 'alt', ellipsis: true },
            { title: 'Order', dataIndex: 'sortOrder', width: 72 },
        ],
        sectionExtraFields: (
            <Form.Item name="backgroundColor" label={t('admin.fields.backgroundColor')}>
                <Input placeholder="#f4f2e8" />
            </Form.Item>
        ),
        itemExtraFields: (
            <>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 500 }}>
                        {t('admin.fields.image')}{' '}
                        {!galleryImageEditing ? (
                            <span style={{ color: '#DC2626' }}>*</span>
                        ) : (
                            <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>
                                {t('admin.fields.imageOptional')}
                            </span>
                        )}
                    </div>
                    <input
                        ref={galleryFileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'block' }}
                        onChange={(e) => setGalleryImageFile(e.target.files?.[0] ?? null)}
                    />
                    {galleryImageEditing?.imageUrl && (
                        <div style={{ marginTop: 10 }}>
                            <img
                                src={
                                    galleryImageEditing.imageUrl.startsWith('http')
                                        ? galleryImageEditing.imageUrl
                                        : `${(process.env.REACT_APP_API_URL ?? '').replace(/\/api\/?$/, '')}${galleryImageEditing.imageUrl}`
                                }
                                alt=""
                                style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8 }}
                            />
                        </div>
                    )}
                </div>
                <Form.Item name="alt" label={t('admin.fields.alt')}>
                    <Input />
                </Form.Item>
                <Form.Item name="sortOrder" label={t('admin.fields.sortOrder')}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
            </>
        ),
    })

    const BenefitsTab = makeSectionCrudTab<BenefitsSection>({
        title: t('admin.tabs.benefits'),
        sections: benefitsSections,
        selectedId: benefitsSectionId,
        setSelectedId: setBenefitsSectionId,
        sectionModalOpen: benefitsSectionModalOpen,
        setSectionModalOpen: setBenefitsSectionModalOpen,
        sectionEditing: benefitsSectionEditing,
        setSectionEditing: setBenefitsSectionEditing,
        sectionForm: benefitsSectionForm,
        createSection: (values) => safePost('/benefits', values),
        updateSection: (id, values) => safePatch(`/benefits/${id}`, values),
        removeSection: (id) => safeDelete(`/benefits/${id}`),
        itemsTitle: t('admin.items.items'),
        items: benefitItems,
        itemsLoading: benefitItemsLoading,
        reloadItems: reloadBenefitItems,
        itemModalOpen: benefitItemModalOpen,
        setItemModalOpen: setBenefitItemModalOpen,
        itemEditing: benefitItemEditing,
        setItemEditing: setBenefitItemEditing,
        itemForm: benefitItemForm,
        createItem: (values) => safePost('/benefits/items', values),
        updateItem: (id, values) => safePatch(`/benefits/items/${id}`, values),
        removeItem: (id) => safeDelete(`/benefits/items/${id}`),
        sectionIdField: 'sectionId',
        itemColumns: [
            { title: t('admin.fields.titleRu'), dataIndex: 'titleRu', ellipsis: true },
            { title: t('admin.fields.titleEn'), dataIndex: 'titleEn', ellipsis: true },
            { title: t('admin.fields.icon'), dataIndex: 'icon', width: 60 },
            { title: t('admin.fields.sortOrder'), dataIndex: 'sortOrder', width: 80 },
        ],
        itemExtraFields: (
            <>
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="titleRu" label={t('admin.fields.titleRu')} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="titleEn" label={t('admin.fields.titleEn')} rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="descriptionRu" label={t('admin.fields.descriptionRu')} rules={[{ required: true }]}>
                            <Input.TextArea rows={3} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="descriptionEn" label={t('admin.fields.descriptionEn')} rules={[{ required: true }]}>
                            <Input.TextArea rows={3} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="icon" label={t('admin.fields.icon')}>
                            <Input placeholder="🛣️" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="sortOrder" label={t('admin.fields.sortOrder')}>
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
            </>
        ),
    })

    // ── Reviews Tab ──────────────────────────────────────────────
    const serverUrl = (process.env.REACT_APP_API_URL ?? '').replace(/\/api\/?$/, '')

    const ReviewsTab = (
        <div className={cls.tabContent}>
            <div className={cls.sectionCard}>
                <div className={cls.cardHeader}>
                    <span className={cls.cardHeaderTitle}>{t('admin.tabs.reviews')}</span>
                    <div className={cls.cardHeaderActions}>
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={pendingReviews.reload}
                            loading={pendingReviews.loading}
                        >
                            {t('admin.actions.refresh')}
                        </Button>
                    </div>
                </div>
                <div className={cls.tableWrapper}>
                    <Table<Review>
                        rowKey="id"
                        loading={pendingReviews.loading}
                        dataSource={pendingReviews.data}
                        pagination={false}
                        size="middle"
                        scroll={{ x: 600 }}
                        columns={[
                            {
                                title: 'Фото',
                                dataIndex: 'imageUrl',
                                width: 72,
                                render: (url: string | null) => {
                                    if (!url) return <span style={{ color: '#9ca3af', fontSize: 12 }}>{t('admin.reviews.noImage')}</span>
                                    const fullUrl = url.startsWith('http') ? url : `${serverUrl}${url}`
                                    return (
                                        <img
                                            src={fullUrl}
                                            alt=""
                                            style={{ width: 56, height: 38, objectFit: 'cover', borderRadius: 6 }}
                                        />
                                    )
                                },
                            },
                            { title: 'Имя', dataIndex: 'name', width: 140, ellipsis: true },
                            {
                                title: 'Текст',
                                dataIndex: 'text',
                                ellipsis: true,
                                render: (text: string) => (
                                    <span title={text}>
                                        {text.length > 80 ? `${text.slice(0, 80)}…` : text}
                                    </span>
                                ),
                            },
                            {
                                title: 'Дата',
                                dataIndex: 'createdAt',
                                width: 130,
                                render: (val: string) =>
                                    new Date(val).toLocaleDateString('ru-RU', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                    }),
                            },
                            {
                                title: '',
                                width: 120,
                                fixed: 'right' as const,
                                render: (_, record) => (
                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                        {record.approved ? '' : <Button
                                            size="small"
                                            type="primary"
                                            loading={approvingId === record.id}
                                            onClick={async () => {
                                                setApprovingId(record.id)
                                                try {
                                                    await safePatch(`/reviews/${record.id}/approve`, {})
                                                    await pendingReviews.reload()
                                                } finally {
                                                    setApprovingId(null)
                                                }
                                            }}
                                        >
                                            {t('admin.reviews.approve')}
                                        </Button>}
                                     
                                        <Popconfirm
                                            title={t('admin.confirm.deleteRecord')}
                                            okText={t('admin.confirm.yes')}
                                            cancelText={t('admin.confirm.no')}
                                            onConfirm={async () => {
                                                await safeDelete(`/reviews/${record.id}`)
                                                await pendingReviews.reload()
                                            }}
                                        >
                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            </div>
        </div>
    )

    // ── Theme Tab ────────────────────────────────────────────────
    const THEME_DEFAULTS: Required<Omit<SiteTheme, 'fontSizeBody' | 'fontSizeH1' | 'fontSizeH2'>> & {
        fontSizeBody: number
        fontSizeH1: number
        fontSizeH2: number
    } = {
        bgBase: '#FAFAF7',
        bgSurface: '#FFFFFF',
        bgMuted: '#F4EFE6',
        bgTint: '#EDE5D8',
        bgDark: '#111110',
        textPrimary: '#1C1917',
        textSecondary: '#78716C',
        textTertiary: '#A8A29E',
        accentPrimary: '#E97316',
        accentPrimaryHover: '#C2610D',
        accentSuccess: '#16A34A',
        fontSizeBody: 16,
        fontSizeH1: 56,
        fontSizeH2: 36,
    }

    const [themeValues, setThemeValues] = React.useState<SiteTheme>(THEME_DEFAULTS)
    const [themeLoading, setThemeLoading] = React.useState(false)
    const colorInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({})

    React.useEffect(() => {
        $api.get<SiteTheme>('/site-theme')
            .then((res) => {
                const merged: SiteTheme = { ...THEME_DEFAULTS }
                const data = res.data
                if (data.bgBase) merged.bgBase = data.bgBase
                if (data.bgSurface) merged.bgSurface = data.bgSurface
                if (data.bgMuted) merged.bgMuted = data.bgMuted
                if (data.bgTint) merged.bgTint = data.bgTint
                if (data.bgDark) merged.bgDark = data.bgDark
                if (data.textPrimary) merged.textPrimary = data.textPrimary
                if (data.textSecondary) merged.textSecondary = data.textSecondary
                if (data.textTertiary) merged.textTertiary = data.textTertiary
                if (data.accentPrimary) merged.accentPrimary = data.accentPrimary
                if (data.accentPrimaryHover) merged.accentPrimaryHover = data.accentPrimaryHover
                if (data.accentSuccess) merged.accentSuccess = data.accentSuccess
                if (data.fontSizeBody) merged.fontSizeBody = data.fontSizeBody
                if (data.fontSizeH1) merged.fontSizeH1 = data.fontSizeH1
                if (data.fontSizeH2) merged.fontSizeH2 = data.fontSizeH2
                setThemeValues(merged)
            })
            .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleThemeSave = async () => {
        setThemeLoading(true)
        try {
            await $api.patch('/site-theme', themeValues)
            applySiteTheme(themeValues)
            message.success(t('admin.theme.saveSuccess'))
        } catch {
            message.error('Error')
        } finally {
            setThemeLoading(false)
        }
    }

    const handleThemeReset = async () => {
        setThemeLoading(true)
        try {
            await $api.post('/site-theme/reset')
            setThemeValues(THEME_DEFAULTS)
            applySiteTheme({
                bgBase: null, bgSurface: null, bgMuted: null, bgTint: null, bgDark: null,
                textPrimary: null, textSecondary: null, textTertiary: null,
                accentPrimary: null, accentPrimaryHover: null, accentSuccess: null,
                fontSizeBody: null, fontSizeH1: null, fontSizeH2: null,
            })
            message.success(t('admin.theme.resetSuccess'))
        } catch {
            message.error('Error')
        } finally {
            setThemeLoading(false)
        }
    }

    const colorFields: Array<keyof Pick<SiteTheme,
        'bgBase' | 'bgSurface' | 'bgMuted' | 'bgTint' | 'bgDark' |
        'textPrimary' | 'textSecondary' | 'textTertiary' |
        'accentPrimary' | 'accentPrimaryHover' | 'accentSuccess'
    >> = [
        'bgBase', 'bgSurface', 'bgMuted', 'bgTint', 'bgDark',
        'textPrimary', 'textSecondary', 'textTertiary',
        'accentPrimary', 'accentPrimaryHover', 'accentSuccess',
    ]

    const fontFields: Array<{
        key: keyof Pick<SiteTheme, 'fontSizeBody' | 'fontSizeH1' | 'fontSizeH2'>
        min: number
    }> = [
        { key: 'fontSizeBody', min: 8 },
        { key: 'fontSizeH1', min: 16 },
        { key: 'fontSizeH2', min: 16 },
    ]

    const ThemeTab = (
        <div className={cls.themeSection}>
            <p className={cls.themeDesc}>{t('admin.theme.subtitle')}</p>

            <div className={cls.themeBlock}>
                <div className={cls.themeBlockHead}>{t('admin.theme.colorsTitle')}</div>
                <div className={cls.themeBlockBody}>
                    <div className={cls.colorGrid}>
                        {colorFields.map((field) => (
                            <div key={field} className={cls.colorField}>
                                <span className={cls.colorLabel}>
                                    {t(`admin.theme.fields.${field}`)}
                                </span>
                                <div className={cls.colorRow}>
                                    <button
                                        type="button"
                                        className={cls.colorSwatch}
                                        style={{ background: themeValues[field] as string ?? '#fff' }}
                                        onClick={() => colorInputRefs.current[field]?.click()}
                                        title={t(`admin.theme.fields.${field}`)}
                                    />
                                    <input
                                        ref={(el) => { colorInputRefs.current[field] = el }}
                                        type="color"
                                        value={themeValues[field] as string ?? '#ffffff'}
                                        onChange={(e) =>
                                            setThemeValues((prev) => ({ ...prev, [field]: e.target.value }))
                                        }
                                        style={{
                                            position: 'absolute',
                                            opacity: 0,
                                            width: 0,
                                            height: 0,
                                            pointerEvents: 'none',
                                        }}
                                    />
                                    <span className={cls.colorValue}>
                                        {themeValues[field] as string}
                                    </span>
                                </div>
                            </ div>
                        ))}
                    </div>
                </div>
            </div>

            <div className={cls.themeBlock}>
                <div className={cls.themeBlockHead}>{t('admin.theme.fontsTitle')}</div>
                <div className={cls.themeBlockBody}>
                    <div className={cls.fontGrid}>
                        {fontFields.map(({ key, min }) => (
                            <div key={key} className={cls.fontField}>
                                <span className={cls.fontLabel}>
                                    {t(`admin.theme.fields.${key}`)}
                                </span>
                                <InputNumber
                                    min={min}
                                    max={120}
                                    value={themeValues[key] as number}
                                    onChange={(v) =>
                                        setThemeValues((prev) => ({ ...prev, [key]: v ?? min }))
                                    }
                                    addonAfter="px"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className={cls.themeActions}>
                    <Button type="primary" loading={themeLoading} onClick={handleThemeSave}>
                        {t('admin.theme.save')}
                    </Button>
                    <Popconfirm
                        title={t('admin.theme.reset')}
                        description="Все цвета и размеры шрифтов вернутся к значениям по умолчанию"
                        okText={t('admin.confirm.yes')}
                        cancelText={t('admin.confirm.no')}
                        onConfirm={handleThemeReset}
                    >
                        <Button loading={themeLoading}>{t('admin.theme.reset')}</Button>
                    </Popconfirm>
                </div>
            </div>
        </div>
    )

    // ── Render ───────────────────────────────────────────────────
    return (
        <div className={cls.adminWrapper}>
            <StickyHeader onPrimaryCtaClick={() => { window.location.href = '/' }} />
            <div className={cls.adminBody}>
                <div className={cls.adminHeader}>
                    <div className={cls.adminBadge}>
                        <SettingOutlined />
                        Admin Panel
                    </div>
                    <Typography.Title level={2} className={cls.adminTitle}>
                        {t('admin.pageTitle')}
                    </Typography.Title>
                    <p className={cls.adminSubtitle}>{t('admin.pageSubtitle')}</p>
                </div>

                <div className={cls.tabsContainer}>
                    <Tabs
                        className={cls.adminTabs}
                        tabPosition={isMobile ? 'top' : 'left'}
                        destroyInactiveTabPane={false}
                        items={[
                            {
                                key: 'contact',
                                label: (
                                    <span className={cls.tabLabel}>
                                        <PhoneOutlined />
                                        {t('admin.tabs.contact')}
                                    </span>
                                ),
                                children: ContactTab,
                            },
                            {
                                key: 'about',
                                label: (
                                    <span className={cls.tabLabel}>
                                        <InfoCircleOutlined />
                                        {t('admin.tabs.about')}
                                    </span>
                                ),
                                children: AboutTab,
                            },
                            {
                                key: 'features',
                                label: (
                                    <span className={cls.tabLabel}>
                                        <StarOutlined />
                                        {t('admin.tabs.features')}
                                    </span>
                                ),
                                children: FeaturesTab,
                            },
                            {
                                key: 'details',
                                label: (
                                    <span className={cls.tabLabel}>
                                        <AppstoreOutlined />
                                        {t('admin.tabs.details')}
                                    </span>
                                ),
                                children: DetailsTab,
                            },
                            {
                                key: 'price',
                                label: (
                                    <span className={cls.tabLabel}>
                                        <DollarOutlined />
                                        {t('admin.tabs.price')}
                                    </span>
                                ),
                                children: PriceTab,
                            },
                            {
                                key: 'gallery',
                                label: (
                                    <span className={cls.tabLabel}>
                                        <PictureOutlined />
                                        {t('admin.tabs.gallery')}
                                    </span>
                                ),
                                children: GalleryTab,
                            },
                            {
                                key: 'benefits',
                                label: (
                                    <span className={cls.tabLabel}>
                                        <CheckCircleOutlined />
                                        {t('admin.tabs.benefits')}
                                    </span>
                                ),
                                children: BenefitsTab,
                            },
                            {
                                key: 'reviews',
                                label: (
                                    <span className={cls.tabLabel}>
                                        <MessageOutlined />
                                        {t('admin.tabs.reviews')}
                                    </span>
                                ),
                                children: ReviewsTab,
                            },
                            {
                                key: 'theme',
                                label: (
                                    <span className={cls.tabLabel}>
                                        <BgColorsOutlined />
                                        {t('admin.tabs.theme')}
                                    </span>
                                ),
                                children: ThemeTab,
                            },
                        ]}
                    />
                </div>
            </div>
        </div>
    )
}

export default AdminPage
